import React from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin } from "lucide-react";
import { SITE } from "@/lib/seo";

const NAV = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "FAQ", to: "/faq" },
  { label: "Contact", to: "/contact" },
];

export const MarketingHeader = () => (
  <header className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-border">
    <div className="max-w-6xl mx-auto px-4 h-16 flex items-center gap-3">
      <Link to="/" className="flex items-center gap-2.5" data-testid="brand-home-link" aria-label="BiteGoFood home">
        <img src="/bitego-icon.png" alt="BiteGoFood logo" width="36" height="36" className="w-9 h-9 rounded-xl" />
        <span className="font-display font-extrabold text-xl text-ink tracking-tight">BiteGoFood</span>
      </Link>
      <nav className="hidden md:flex items-center gap-1 ml-6" aria-label="Primary">
        {NAV.map((n) => (
          <Link key={n.to} to={n.to} className="px-3 py-2 text-sm font-semibold text-ink-600 hover:text-coral transition-colors" data-testid={`nav-${n.label.toLowerCase()}`}>{n.label}</Link>
        ))}
      </nav>
      <div className="ml-auto flex items-center gap-2">
        <Link to="/partner/register" className="hidden sm:inline-flex px-3 py-2 text-sm font-semibold text-ink-600 hover:text-coral transition-colors" data-testid="nav-partner">For Restaurants</Link>
        <Link to="/rider/register" className="hidden sm:inline-flex px-3 py-2 text-sm font-semibold text-ink-600 hover:text-coral transition-colors" data-testid="nav-rider">For Riders</Link>
        <Link to="/login" className="px-4 py-2 rounded-full text-sm font-bold text-ink border border-border hover:border-coral hover:text-coral transition-colors" data-testid="header-login">Log in</Link>
        <Link to="/signup" className="px-4 py-2 rounded-full text-sm font-bold text-white bg-coral hover:bg-coral-hover transition-colors" data-testid="header-signup">Sign up</Link>
      </div>
    </div>
  </header>
);

export const MarketingFooter = () => (
  <footer className="bg-ink text-white/80 mt-20">
    <div className="max-w-6xl mx-auto px-4 py-12 grid gap-8 md:grid-cols-4">
      <div className="md:col-span-2">
        <div className="flex items-center gap-2.5">
          <img src="/bitego-icon.png" alt="BiteGoFood" width="34" height="34" className="w-8 h-8 rounded-lg" />
          <span className="font-display font-extrabold text-lg text-white">BiteGoFood</span>
        </div>
        <p className="text-sm mt-3 max-w-sm">{SITE.tagline} BiteGoFood is a food delivery platform connecting customers with local restaurants and delivery partners.</p>
        <p className="text-xs mt-4 text-white/60">A Product by {SITE.company}</p>
        <p className="text-xs text-white/60">Founder &amp; CEO: {SITE.founder}</p>
      </div>
      <div>
        <h3 className="text-white font-bold text-sm mb-3">Company</h3>
        <ul className="space-y-2 text-sm">
          <li><Link to="/about" className="hover:text-coral">About BiteGoFood</Link></li>
          <li><Link to="/about/bitegofood" className="hover:text-coral">What is BiteGoFood?</Link></li>
          <li><Link to="/faq" className="hover:text-coral">FAQ</Link></li>
          <li><Link to="/contact" className="hover:text-coral">Contact</Link></li>
          <li><a href={SITE.adsUrl} target="_blank" rel="noopener noreferrer" className="hover:text-coral">BiteGoFood Ads &amp; Promotions</a></li>
          <li><Link to="/privacy" className="hover:text-coral">Privacy Policy</Link></li>
          <li><Link to="/terms" className="hover:text-coral">Terms</Link></li>
        </ul>
      </div>
      <div>
        <h3 className="text-white font-bold text-sm mb-3">Get started</h3>
        <ul className="space-y-2 text-sm">
          <li><Link to="/signup" className="hover:text-coral">Order food</Link></li>
          <li><Link to="/partner/register" className="hover:text-coral">Add your restaurant</Link></li>
          <li><Link to="/rider/register" className="hover:text-coral">Become a delivery partner</Link></li>
        </ul>
        <div className="mt-4 space-y-1.5 text-sm">
          <a href={`mailto:${SITE.email}`} className="flex items-center gap-2 hover:text-coral"><Mail size={14} /> {SITE.email}</a>
          <span className="flex items-center gap-2 text-white/60"><MapPin size={14} /> Available in select cities — and growing</span>
        </div>
        {SITE.social.length > 0 && (
          <div className="flex gap-3 mt-4">
            {SITE.social.map((s) => <a key={s} href={s} target="_blank" rel="noopener noreferrer" className="text-sm hover:text-coral">{new URL(s).hostname.replace("www.", "")}</a>)}
          </div>
        )}
      </div>
    </div>
    <div className="border-t border-white/10">
      <div className="max-w-6xl mx-auto px-4 py-4 text-xs text-white/50 flex flex-col sm:flex-row justify-between gap-2">
        <span>© {new Date().getFullYear()} {SITE.company}. All rights reserved.</span>
        <span>BiteGoFood — {SITE.tagline}</span>
      </div>
    </div>
  </footer>
);

export const MarketingShell = ({ children }) => (
  <div className="min-h-screen bg-white text-ink flex flex-col">
    <MarketingHeader />
    <main className="flex-1">{children}</main>
    <MarketingFooter />
  </div>
);
